/**
 * Format an ISO timestamp in Canada Eastern Time (America/Toronto).
 *
 * Background (May 2026): backend stores every email/audit timestamp in
 * UTC via `datetime.now(timezone.utc).isoformat()`. Pre-fix the modals
 * just chopped the ISO string and replaced 'T' with ' ', so operators
 * saw UTC times instead of local — an email sent at 2026-05-13 20:10
 * Toronto rendered as 2026-05-14 00:10 in Send history.
 *
 * Using America/Toronto (not a fixed UTC-5 offset) so the auto-DST
 * cutover handles the EST↔EDT switch every March/November without
 * code churn.
 *
 * @param {string|Date|null|undefined} value ISO 8601 string or Date.
 * @returns "YYYY-MM-DD HH:mm" in Toronto local time, or "" on bad input.
 */
export function formatTorontoTimestamp(value) {
  if (!value) return "";
  let d;
  try {
    d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
  } catch {
    return "";
  }
  // en-CA on America/Toronto returns "YYYY-MM-DD, HH:mm" — we drop the
  // comma so the format matches what operators have been reading for
  // the past 9 months (and what the rest of the audit UI uses).
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Toronto",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    // `hourCycle: "h23"` forces midnight to render as "00:00" not "24:00".
    // Without it Node's ICU build returns "24:00" for some locales and
    // operators would see the wrong day boundary at midnight Toronto.
    hourCycle: "h23",
  });
  return fmt.format(d).replace(", ", " ");
}

export default formatTorontoTimestamp;
